import { Box, Checkbox, FormControlLabel, Grid, MenuItem, Select, TextField } from "@mui/material";
import * as React from "react";
import { Base } from "ui/Scripts/source/framework/base";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { ISessionState } from "../../../models/session/sessionState";
import { EditItem } from "ui/Scripts/source/models/common/editItem";
import { IFileAttachment } from "ui/Scripts/source/models/common/fileAttachment";
import { IIdTitle, IdTitle } from "ui/Scripts/source/models/common/idTitle";
import { ConfirmationDialogResult, RecordUserAttachmentCategory } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import { SaveDataObj } from "../../../framework/saveData";
import { IRecordEditItemFamiliarization, RecordEditItemFamiliarization } from "../../../models/record/recordEditItemFamiliarization";
import { IRecordFamiliarizationListItem } from "ui/Scripts/source/models/recordFamiliarization/recordFamiliarizationListItem";
import * as recordService from "../../../models/services/recordService";
import { StoreUtils } from "../../../models/store/storeUtils";
import { DatePicker } from "../../framework/DateTime/DatePicker";
import { Dialog } from "../../framework/Dialog/Dialog";
import { DialogButtons } from "../../framework/Dialog/DialogButtons";
import { DialogItemsNav } from "../../framework/Dialog/DialogItemsNav";
import { Autocomplete } from "../../framework/Form/Autocomplete";
import { FormField } from "../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/components/framework/FormField";
import { GridRow } from "../../framework/Grid/GridRow";
import { UserBlocks } from "source/components/user/UserBlocks/UserBlocks";
import { IModifyUser, ModifyUser } from "ui/Scripts/source/models/user/modifyUser";
import { UserListItems } from "source/models/user/userListItems";
import { RecordEditItemUserFamiliarizationSave } from "ui/Scripts/source/models/recordUserFamiliarization/recordEditItemUserFamiliarization";
import { UserListItem } from "source/models/user/userListItem";
import { RecordEditAttachmentList } from "../RecordEdit/RecordEditAttachmentList";

export interface IRecordUserFamiliarizationDialogProp {
    classes?: string;
    sessionState: ISessionState;
    companyId: string;
    recordId: string;
    unitId: string;
    userId: string;
    userCode: string;
    familiarizations: IRecordFamiliarizationListItem[];
    familiarizationTrainers: string[];
    item: IRecordEditItemFamiliarization;
    attachments: IFileAttachment[];
    hasPrevious: boolean;
    hasNext: boolean;
    onCustomSave?: (saveItem: RecordEditItemUserFamiliarizationSave) => Promise<ApiSuccess>;
    onSaved: (item: IRecordEditItemFamiliarization, closeDialog: boolean) => void;
    onClose: (handlers: IModifyUser[]) => void;
    onPrevious: () => void;
    onNext: () => void;
    onAttachmentsRemoved: (ids: string[]) => void;
    onAttachmentsAdded: (attachments: IFileAttachment[]) => void;
}

export interface IRecordUserFamiliarizationDialogState {
    familiarizationId: string;
    startDate: string;
    endDate: string;
    validityEndDate: string;
    trainer: string;
    comment: string;
    activeState: number;
    familiarizationTrainers: IIdTitle[];
    attachments: IFileAttachment[];
    handlers: IModifyUser[];
}

export class RecordUserFamiliarizationDialog extends React.Component<IRecordUserFamiliarizationDialogProp, IRecordUserFamiliarizationDialogState> {
    private orgStateHash: string = "";
    
    getStateFromProps = (props: IRecordUserFamiliarizationDialogProp): IRecordUserFamiliarizationDialogState => {
        const item = props.item;
        const familiarizationTrainers = props.familiarizationTrainers.slice(0);
        familiarizationTrainers.sort(Base.strCompare);
        return {
            familiarizationId: item.familiarizationId,
            startDate: Base.utcTimeToDateStr(item.startDate),
            endDate: Base.utcTimeToDateStr(item.endDate),
            validityEndDate: Base.utcTimeToDateStr(item.validityEndDate),
            trainer: item.trainer,
            comment: item.comment,
            activeState: item.activeState,
            familiarizationTrainers: familiarizationTrainers.map(i => IdTitle.createIdTitleSimple(i)),
            attachments: props.attachments.filter(i => i.subOwnerId === item.id),
            handlers: item.handlers.slice(0),
        };
    };

    constructor(props: IRecordUserFamiliarizationDialogProp) {
        super(props);
        this.state = this.getStateFromProps(props);
        this.orgStateHash = this.getHashFromState(props, this.state);
    }

    async componentDidUpdate(prevProps: IRecordUserFamiliarizationDialogProp, prevState: IRecordUserFamiliarizationDialogState) {
        const props = this.props;
        if (prevProps.item.getHash() === props.item.getHash() && EditItem.getHash(prevProps.attachments) === EditItem.getHash(props.attachments)) return;
        const state = this.getStateFromProps(props);
        this.setState(state);
        this.orgStateHash = this.getHashFromState(props, state);
    }

    handleChange = (event) => {
        const target = event.target;
        const value: string = target.value;
        const name: string = target.name;
        if (name === "familiarizationId") {
            this.setState({ familiarizationId: value });
        } else if (name === "comment") {
            this.setState({ comment: value });
        }
    };

    handleTrainerAutoCompleteChange = (value: string) => {
        this.setState({ trainer: value });
    };

    dateChange = (startDate: Date, endDate: Date) => {
        if (!Base.isNullOrUndefined(startDate) && !Base.isNullOrUndefined(endDate) && endDate < startDate) {
            endDate = startDate;
        }
        const startDateStr = Base.utcTimeToDateStr(startDate.getTime());
        const endDateStr = endDate ? Base.utcTimeToDateStr(endDate.getTime()) : "";
        this.setState({
            startDate: startDateStr,
            endDate: endDateStr
        });
    };

    handleStartDateChange = (value: string) => {
        this.setState({ startDate: value });
    };

    handleStartDateBlur = (value: string) => {
        const state = this.state;
        this.dateChange(value ? value.toUtcDate() : Base.getNowUtcDate().addDays(-1), state.endDate ? state.endDate.toUtcDate() : null);
    };

    handleEndDateChange = (value: string) => {
        this.setState({ endDate: value });
    };

    handleEndDateBlur = (value: string) => {
        const state = this.state;
        this.dateChange(state.startDate ? state.startDate.toUtcDate() : Base.getNowUtcDate().addDays(-1), value ? value.toUtcDate() : null);
    };

    handleValidityEndDateChange = (value: string) => {
        this.setState({ validityEndDate: value });
    };

    handleValidityEndDateBlur = (value: string) => {
        this.setState({
            validityEndDate: value ? Base.utcTimeToDateStr(value.toUtcDate().getTime()) : ""
        });
    };

    getHashFromState = (props: IRecordUserFamiliarizationDialogProp, state: IRecordUserFamiliarizationDialogState): string => {
        const result = new SaveDataObj();
        result.append("familiarizationId", state.familiarizationId);
        result.append("startDate", state.startDate);
        result.append("endDate", !state.endDate ? null : state.endDate);
        result.append("validityEndDate", !state.validityEndDate ? null : state.validityEndDate);
        result.append("trainer", state.trainer);
        result.append("comment", state.comment);
        result.append("activeState", state.activeState);
        return result.hash;
    };

    getSaveData = (props: IRecordUserFamiliarizationDialogProp, recordEditItemUserFamiliarizationSave: RecordEditItemUserFamiliarizationSave): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("id", props.recordId);
        result.append("companyId", props.companyId);
        result.append("userCode", props.userCode);
        result.append("familiarization", recordEditItemUserFamiliarizationSave);
        return result;
    };

    saveToDb = async (): Promise<RecordEditItemFamiliarization> => {
        const props = this.props;
        const state = this.state;
        // Set data
        const isNew = props.item.isNew();
        const item = new RecordEditItemFamiliarization(this.props.item);
        item.familiarizationId = state.familiarizationId;
        item.startDate = state.startDate.toUtcDate().getTime();
        item.endDate = state.endDate ? state.endDate.toUtcDate().getTime() : null;
        item.validityEndDate = state.validityEndDate ? state.validityEndDate.toUtcDate().getTime() : null;
        item.trainer = state.trainer;
        item.comment = state.comment;
        item.activeState = state.activeState;
        item.handlers = state.handlers.map(i => new ModifyUser(i));
        // Set save data
        const recordFamiliarizationSave = new RecordEditItemUserFamiliarizationSave(item);
        recordFamiliarizationSave.handlers = isNew
            ? state.handlers.map(i => { return { userCode: i.code, approved: i.date > 0 }; })
            : [];
        // Save
        if (props.onCustomSave) {
            const customResult = await props.onCustomSave(recordFamiliarizationSave);
            if (!customResult) return null;
            return item;
        }
        const saveData = this.getSaveData(this.props, recordFamiliarizationSave);
        const result = await AppUtils.callService(() => recordService.recordEditSaveFamiliarization(saveData.obj));
        if (!result) return null;
        // Update item
        item.id = result.id;
        item.rowId = result.rowId;
        item.modifyUser = StoreUtils.getNowModifyUser();
        return item;
    };

    saveDialog = async (close: boolean) => {
        const result = await this.saveToDb();
        if (result) {
            this.props.onSaved(result, close);
        }
        return result;
    };

    // #region Handlers
    handleGetHandlersSelectListItems = async (companyId: string, pageSize: number, page: number, filter: string,
        ignoreUserIds: string[], ignoreUserCodes: string[], activityState: number, ignoreRecordUserConnected: boolean, _ignoreQrCodeUsers: boolean, _requireQrCodeUsers: boolean,
        doNotIgnoreRecordUserConnectedUserCode: string, sortColumn: string, sortOrderIsAsc: boolean): Promise<UserListItems> => {
        const props = this.props;
        //onCustomSave
        const result = await recordService.getFamiliarizationHandlersSelectUserListItems(props.companyId, props.item.id, props.unitId, props.userCode, pageSize, page, filter,
            ignoreUserIds, ignoreUserCodes, activityState, ignoreRecordUserConnected, doNotIgnoreRecordUserConnectedUserCode,
            sortColumn, sortOrderIsAsc);
        if (result && props.onCustomSave) {
            const targetUser = new UserListItem();
            targetUser.id = Base.emptyGuid;
            targetUser.code = Base.emptyGuid;
            targetUser.firstName = Translations.RecordUserUser;
            result.items.unshift(targetUser);
        }
        return result;
    };
        
    updateHandlers = async (newHandlers: IModifyUser[], canRemove: boolean, showSuccessMessage: boolean): Promise<ApiSuccess> => {
        const props = this.props;
        const state = this.state;
        const isNew = props.item.isNew();
        const oldHandlerCodes = state.handlers.map(i => i.code);
        const addHandlers = (newHandlers ?? []).filter(i => oldHandlerCodes.indexOf(i.code) < 0);
        const removeHandlerCodes = canRemove
            ? oldHandlerCodes.filter(i => (newHandlers ?? []).findIndex(j => j.code === i) < 0)
            : [];
        if (addHandlers.length < 1 && removeHandlerCodes.length < 1) return;
        let handlers: IModifyUser[] = state.handlers.slice(0);
        if (removeHandlerCodes.length > 0) {
            if (!isNew) {
                const removeResult = await AppUtils.callService(() => recordService.familiarizationEditRemoveHandlers(props.companyId, props.item.id, props.userCode, removeHandlerCodes), showSuccessMessage);
                if (!removeResult) return null;
            }
            handlers = handlers.filter(i => removeHandlerCodes.indexOf(i.code) < 0);
        }
        if (addHandlers.length > 0) {
            if (!isNew) {
                const addResult = await AppUtils.callService(() => recordService.familiarizationEditAddHandlers(props.companyId, props.item.id, props.userCode, addHandlers.map(i => i.code)), showSuccessMessage);
                if (!addResult) return null;
            }
            for (const user of addHandlers) {
                user.date = 0;
            }
            handlers = handlers.concat(addHandlers);
        }
        handlers = ModifyUser.sortModifyUsers(handlers, { currentUserCode: props.userCode });
        this.setState({
            handlers
        });
        return new ApiSuccess();
    };

    addHandlers = async (users: IModifyUser[], showSuccessMessage: boolean): Promise<ApiSuccess> => {
        if (!users || users.length < 1) return null;
        return this.updateHandlers(users, false, showSuccessMessage);
    };

    handleAddHandlers = async (users: IModifyUser[]): Promise<ApiSuccess> => {
        return this.addHandlers(users, true);
    };

    handleRemoveHandlers = async (userCodes: string[]): Promise<ApiSuccess> => {
        const state = this.state;
        if (!userCodes || userCodes.length < 1) return null;
        return this.updateHandlers(state.handlers.filter(i => userCodes.indexOf(i.code) < 0), true, true);
    };

    handleApprove = async (userCode: string) => {
        const props = this.props;
        const state = this.state;
        const isNew = props.item.isNew();
        if (!userCode) return;
        const handler = state.handlers.find(i => i.code === userCode);
        if (!handler) return;
        // Call server
        if (!isNew) {
            const result = await AppUtils.callService(() => recordService.approveFamiliarization(props.companyId, props.item.id, props.userCode));
            if (!result) return;
        }
        handler.date = Base.getNowTicks();
        const handlers = ModifyUser.sortModifyUsers(state.handlers.filter(i => i.code !== userCode).concat([new ModifyUser(handler)]), { currentUserCode: props.userCode });
        this.setState({
            handlers
        });
    };
    // #endregion Handlers

    handleSave = async (closeDialog: boolean): Promise<RecordEditItemFamiliarization> => {
        return await this.saveDialog(closeDialog);
    };

    handleCancel = async () => {
        const props = this.props;
        const state = this.state;
        const cancelResult = await AppUtils.cancel(this.getHashFromState(props, state), this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            this.handleSave(true);
        } else {
            props.onClose(state.handlers);
        }
    };

    canMoveOut = async (): Promise<boolean> => {
        const props = this.props;
        return await AppUtils.canMoveOut(this.getHashFromState(props, this.state), this.orgStateHash,
            async () => !!await this.saveDialog(false),
            null,
            (newStateHash) => { this.orgStateHash = newStateHash; }
        );
    };

    handlePrevious = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onPrevious();
    };

    handleNext = async () => {
        if (!await this.canMoveOut()) return;
        this.props.onNext();
    };

    render() {
        const props = this.props;
        const state = this.state;
        const isNew = props.item.isNew();
        return (
            <Dialog
                id="record-user-familiarization-dialog"
                maxWidth="sm"
                title={isNew ? Translations.AddFamiliarization : Translations.EditFamiliarization + ": " + props.item.familiarizationCode}
                body={
                    <>
                        <GridRow>
                            <Grid item xs>
                                <FormField title={Translations.Subject} margin={0}>
                                    <Select name="familiarizationId" title={Translations.Subject} value={state.familiarizationId} onChange={this.handleChange}>
                                        {props.familiarizations.map(familiarization =>
                                            <MenuItem key={familiarization.id} value={familiarization.id}>{familiarization.code}</MenuItem>
                                        )}
                                    </Select>
                                </FormField>
                            </Grid>
                        </GridRow>
                        <GridRow sx={{ alignItems: "end" }}>
                            <Grid item xs>
                                <FormField title={Translations.FamiliarizationTrainer}>
                                    <Autocomplete
                                        value={state.trainer}
                                        maxLength={50}
                                        options={state.familiarizationTrainers}
                                        onChange={this.handleTrainerAutoCompleteChange}
                                    />
                                </FormField>
                            </Grid>
                            <Grid item xs>
                                <FormField title="">
                                    <FormControlLabel
                                        label={Translations.Archived}
                                        control={
                                            <Checkbox
                                                checked={state.activeState < 0.5}
                                                onChange={(_e, checked: boolean) => this.setState({ activeState: checked ? 0 : 1 })}
                                            />
                                        }
                                    />
                                </FormField>
                            </Grid>
                        </GridRow>
                        <GridRow>
                            <Grid item xs>
                                <FormField title={Translations.FamiliarizationStartDate} required>
                                    <DatePicker
                                        value={state.startDate}
                                        required={true}
                                        onChange={this.handleStartDateChange}
                                    />
                                </FormField>
                            </Grid>
                            <Grid item xs>
                                <FormField title={Translations.FamiliarizationEndDate}>
                                    <DatePicker
                                        value={state.endDate}
                                        onChange={this.handleEndDateChange}
                                    />
                                </FormField>
                            </Grid>
                            <Grid item xs>
                                <FormField title={Translations.ValidityEndDate}>
                                    <DatePicker
                                        value={state.validityEndDate}
                                        onChange={this.handleValidityEndDateChange}
                                    />
                                </FormField>
                            </Grid>
                        </GridRow>
                        <GridRow>
                            <Grid item xs>
                                <FormField title={Translations.AdditionalInformation}>
                                    <TextField name="comment" title={Translations.AdditionalInformation} inputProps={{ maxLength: 1000 }} multiline minRows={3} disabled={false} value={state.comment} onChange={this.handleChange} />
                                </FormField>
                            </Grid>
                        </GridRow>
                        <GridRow>
                            <Grid item xs>
                                <UserBlocks
                                    companyId={props.companyId}
                                    userCode={props.sessionState.code}
                                    title={Translations.Approvers}
                                    noUsersMessage={Translations.AddApprovers}
                                    users={state.handlers}
                                    maxHeight="100%"
                                    removeUsersConfirmation={Translations.DoYouReallyWantToRemoveSelectedApprovers}
                                    initiallySelectedIds={state.handlers.length > 0 ? [] : [props.userId]}
                                    onAddUsers={this.handleAddHandlers}
                                    onRemoveUsers={this.handleRemoveHandlers}
                                    onGetUserSelectListItems={this.handleGetHandlersSelectListItems}
                                    onApprove={this.handleApprove}
                                />
                            </Grid>
                        </GridRow>
                        {!!props.onAttachmentsAdded &&
                            <GridRow>
                                <Grid item xs>
                                    <RecordEditAttachmentList
                                        isDisabled={isNew}
                                        isDisabledMessage={Translations.NewFamiliarizationMustBeSavedBeforeAttachmentsCanBeAdded}
                                        companyId={props.companyId}
                                        recordId={props.recordId}
                                        subOwnerId={props.item.id}
                                        userCode={props.userCode}
                                        category={RecordUserAttachmentCategory.Familiarization}
                                        attachments={state.attachments}
                                        onRemoved={props.onAttachmentsRemoved}
                                        onAdded={props.onAttachmentsAdded}
                                    />
                                </Grid>
                            </GridRow>
                        }
                    </>
                }
                actions={
                    <Box display="flex" justifyContent="space-between" width="100%">
                        <DialogItemsNav
                            modifyUser={!isNew ? props.item.modifyUser : null}
                            hasPrevious={props.hasPrevious}
                            hasNext={props.hasNext}
                            onPrevious={!isNew ? this.handlePrevious : null}
                            onNext={!isNew ? this.handleNext : null}
                        />
                        <DialogButtons
                            isNew={isNew}
                            onCancel={this.handleCancel}
                            onSave={() => this.handleSave(false)}
                        />
                    </Box>
                }
                onClose={this.handleCancel}
            />
        );
    }
}